import { memoizedGroupBySelector } from '@travelnest/entities';
import { IProperty } from '../../../data/entities/property';
import { IPropertySummary } from '../../../data/entities/property-summary/index';
import { actions as entityActions } from '../../../data/store';
import { getCustomerContracts } from '../../../entities/contracts/actions';
import { getProperty } from '../../../entities/properties/selectors';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../../store/actions';
import { ThunkAction } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { IAction } from '../../components/action/types';
import { FirstParameter, TailParameters } from '../../helpers/typing';
import { buildActions, buildPropertyActions } from './details/helpers';

export const getCustomerActionsActions = createActionCreators<
	{ T: 'CUSTOMER_GET_ACTIONS_REQUEST', P: string },
	{ T: 'CUSTOMER_GET_ACTIONS_RECEIVE', P: { customerId: string, actions: IAction[] } },
	{ T: 'CUSTOMER_GET_ACTIONS_FAILURE', P: { customerId: string, error: Error } }
>('CUSTOMER_GET_ACTIONS_REQUEST', 'CUSTOMER_GET_ACTIONS_RECEIVE', 'CUSTOMER_GET_ACTIONS_FAILURE');

// properties can exist in data.facts.byPropertyByType and be empty
// if so then we default to location page
const getCompletenessFactsForProperties = (state: IState, ...activePropertySummaries: IPropertySummary[]) =>
	activePropertySummaries.reduce(
		(all, current) => ({
			...all,
			[current.id]: state.data.facts.byPropertyByType[current.id]?.property_onboard_completeness?.context?.page || 'location',
		}), {});

export const getCustomerActions = (customerId: string): ThunkAction => async (dispatch, getState) => {
	dispatch(getCustomerActionsActions.CUSTOMER_GET_ACTIONS_REQUEST(customerId));
	try {
		await Promise.all([
			dispatch(entityActions.customer.getById(customerId)),
			dispatch(entityActions.propertySummary.getByOwnerId(customerId)),
			dispatch(getCustomerContracts(customerId)),
			dispatch(entityActions.notifications.getByOwnerId(customerId)),
		]);
		const state = getState();
		const propertySummaries = memoizedGroupBySelector('owner_id', state.data.propertySummaries.byId)[customerId] || [];
		const activePropertySummaries = propertySummaries.filter((p) => p.is_active) || [] as IPropertySummary[];

		const customer = state.data.customers.byId[customerId];
		const contracts = state.entities.channels.contracts[customerId] || [];

		/*
		reasons for doing the below are that we don't want to fetch the facts
		for the properties we know are completed and also as it stands, the average
		customer only has 1.2 properties therefore we dont want to be generating too
		much network traffic on the off chance they do have lots of properties OR when
		we do dev work and have lots of properties against an account
		*/
		const getActiveIncompletePropertiesWithCompleteness =
			activePropertySummaries.filter((aps) => !aps.is_complete).slice(0, 4).map(
				(aps) => dispatch(entityActions.facts.getByProperty(aps.id))
			);

		await Promise.all(getActiveIncompletePropertiesWithCompleteness);
		const stateWithPropertyFacts = getState();
		const completenessFactsForProperties = getCompletenessFactsForProperties(stateWithPropertyFacts, ...activePropertySummaries);

		const customerActions = buildActions(customer, activePropertySummaries, contracts, completenessFactsForProperties);
		dispatch(getCustomerActionsActions.CUSTOMER_GET_ACTIONS_RECEIVE({ customerId, actions: customerActions }));
	} catch (e) {
		dispatch(getCustomerActionsActions.CUSTOMER_GET_ACTIONS_FAILURE({ customerId, error: e }));
	}
};

export const getPropertyActionsActions = createActionCreators<
	{ T: 'PROPERTY_GET_ACTIONS_REQUEST', P: number },
	{ T: 'PROPERTY_GET_ACTIONS_RECEIVE', P: { propertyId: number, customerId: string, actions: IAction[] } },
	{ T: 'PROPERTY_GET_ACTIONS_FAILURE', P: { propertyId: number, error: Error } }
>('PROPERTY_GET_ACTIONS_REQUEST', 'PROPERTY_GET_ACTIONS_RECEIVE', 'PROPERTY_GET_ACTIONS_FAILURE');

export const getPropertyActions = (propertyId: number): ThunkAction => async (dispatch, getState) => {
	dispatch(getPropertyActionsActions.PROPERTY_GET_ACTIONS_REQUEST(propertyId));
	try {
		await Promise.all([
			dispatch(entityActions.propertySummary.getById(propertyId)),
			dispatch(entityActions.facts.getByProperty(propertyId)),
		]);
		const state = getState();
		const summary = state.data.propertySummaries.byId[propertyId];
		const completenessFactsForProperties = getCompletenessFactsForProperties(state, summary);
		const customerId = summary.owner_id;
		const propertyActions = buildPropertyActions(summary, completenessFactsForProperties);

		dispatch(getPropertyActionsActions.PROPERTY_GET_ACTIONS_RECEIVE({ propertyId, customerId, actions: propertyActions }));
	} catch (e) {
		dispatch(getPropertyActionsActions.PROPERTY_GET_ACTIONS_FAILURE({ propertyId, error: e }));
	}
};

type AnyAction = (...a: any[]) => ThunkAction;
type PropertyAction = (p: IProperty | IProperty['id'], ...a: any[]) => ThunkAction;
const isProperty = (p: any): p is IProperty => typeof p === 'object' && p && p.hasOwnProperty('id');
const isStringOrNumber = (p: any): p is string | number => ['string', 'number'].includes(typeof p);

// Wrap a generic action to refresh the property actions whenever it dispatches.
// The wrapped action must have property or property ID as its first argument

export const withRefreshActionsForProperty = <A extends AnyAction>(action: A) =>
	(propertyId: number, ...p: Parameters<A>): ThunkAction => async (dispatch, getState) => {
		await dispatch(action(...p));

		const property = getProperty(propertyId, getState());
		if (property) {
			dispatch(getCustomerActions(property.owner_id));
		}

		dispatch(getPropertyActions(propertyId));
	};

export const withRefreshActionsForFirstArg = <A extends PropertyAction>(action: A) =>
	(p: FirstParameter<A>, ...other: TailParameters<A>): ThunkAction => async (dispatch, getState) => {
		await dispatch(action(p, ...other));

		let property: IProperty | undefined;

		if (isProperty(p)) {
			property = p;
		} else if (isStringOrNumber(p)) {
			property = getProperty(p, getState());
		}

		if (property) {
			dispatch(getPropertyActions(property.id));
			dispatch(getCustomerActions(property.owner_id));
		}
	};

const actions = { ...getCustomerActionsActions, ...getPropertyActionsActions };
type ActionActions = ActionValues<typeof actions>;
type ActionActionTypes = ActionTypeNames<ActionActions>;

export type Actions = ActionActions[keyof ActionActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as ActionActionTypes);
